import * as React from 'react';
import { $CurrentUser, CurrentUser } from 'app/classes';
import { SalesforceService } from 'app/services';
import { getReferralApp, getProducts } from '../utils';
import { SETTINGS } from 'app/settings';
import * as moment from "moment";
import _ from 'lodash';
import clsx from 'clsx';

import {
  ButtonKind,
  ButtonType,
  GrowlerKind
} from '@spscommerce/ds-shared';
import {
  SpsForm,
  SpsLabel,
  SpsTextInput,
  SpsKeyValueList,
  SpsKeyValueListItem,
  GrowlerConfig,
  SpsGrowler,
  useGrowlers,
  SpsSelect,
  usePatchReducer,
  SpsFocusedTask,
  SpsCard,
  SpsTextarea,
  SpsFocusedTaskActions,
  useSpsForm,
  usePortal
} from "@spscommerce/ds-react";

import { SpsLoadingState } from '../SpsLoadingState';
import './SpsContactForm.scss';

export function SpsContactForm({ icon }: { icon: JSX.Element }) {
  const [growlers, createGrowler] = useGrowlers();

  const fileUploadRef = React.useRef<HTMLDivElement>(null);
  const [state, patchState] = usePatchReducer({
    isOpen: false,
    isLoading: false,
    preselectedProduct: SETTINGS.SF_MANAGED_APPS.OTHER,
    productGroup: 'other',
    displayProducts: [
      SETTINGS.SF_MANAGED_APPS.FULFILLMENT,
      SETTINGS.SF_MANAGED_APPS.FULFILLMENT_MONITOR,
      SETTINGS.SF_MANAGED_APPS.COMMERCE_VAN,
      SETTINGS.SF_MANAGED_APPS.TNC,
      SETTINGS.SF_MANAGED_APPS.ASSORTMENT,
      SETTINGS.SF_MANAGED_APPS.OTHER
    ],
    files: [],
    submitSuccess: false,
    submitClasses: clsx("sps-button", "sps-button--confirm", "ml-1"),
    loading: false
  });

  const SF_MANAGED_APPS = SETTINGS.SF_MANAGED_APPS;

  React.useEffect(() => {
    if (state.preselectedProduct !== SETTINGS.SF_MANAGED_APPS.OTHER) {
      updateForm({ ...formValue, product: state.preselectedProduct });
      setProductGroup(state.preselectedProduct);
    }
  }, [state.preselectedProduct]);

  const initFormValue = {
    additionalInfo: "",
    notificationIdText: "",
    product: state.preselectedProduct,
    reason: "",
    docParcelId: "",
    tradingPartner: "",
  };

  const { formValue, formMeta, updateForm } = useSpsForm(initFormValue);

  const portal = usePortal("sps-contact-form-portal")

  const assortmentOptions = [
    "I have a document question",
    "I need help with a business relationship"
  ]

  const fulfillmentGroupForm = (formMeta) => {
    const options = [
      "I have a document question",
      "I need help with a business relationship"
    ];
    return (
      <>
        <div className="mb-2">
          <SpsLabel for={formMeta.fields.reason}>How can we help you?</SpsLabel>
          <SpsSelect formMeta={formMeta.fields.reason} value={formValue.reason} options={options} />
        </div>

        <div className="mb-2">
          <SpsLabel for={formMeta.fields.docParcelId}>Document or Parcel ID</SpsLabel>
          <SpsTextInput formMeta={formMeta.fields.docParcelId} value={formValue.docParcelId} />
        </div>

        <div className="mb-2">
          <SpsLabel for={formMeta.fields.tradingPartner}>Who is the trading partner?</SpsLabel>
          <SpsTextInput formMeta={formMeta.fields.tradingPartner} value={formValue.tradingPartner} />
        </div>

        <div className="mb-2">
          <SpsLabel for={formMeta.fields.additionalInfo}>Additional Information</SpsLabel>
          <SpsTextarea formMeta={formMeta.fields.additionalInfo} value={formValue.additionalInfo} rows={3} />
        </div>
      </>
    )
  };

  const tncGroupForm = (formMeta) => {
    const options = [
      "I have a product question",
      "I have a spec question",
      "I have a question about an error"
    ];
    return (
      <>
        <div className="mb-2">
          <SpsLabel for={formMeta.fields.reason}>How can we help you?</SpsLabel>
          <SpsSelect formMeta={formMeta.fields.reason} value={formValue.reason} options={options} />
        </div>
        <div className="mb-2">

          <SpsLabel for={formMeta.fields.tradingPartner}>Which retailer is this associated with?</SpsLabel>
          <SpsTextInput formMeta={formMeta.fields.tradingPartner} value={formValue.tradingPartner} />
        </div>
        <div className="mb-2">
          <SpsLabel for={formMeta.fields.additionalInfo}>Additional Information</SpsLabel>
          <SpsTextarea formMeta={formMeta.fields.additionalInfo} value={formValue.additionalInfo} rows={3} />
        </div>
      </>
    )
  };

  const assortmentGroupForm = (formMeta) => {
    const options = [
      "I have a document question",
      "I need help with a business relationship"
    ];
    return (
      <>
        <div className="mb-2">
          <SpsLabel for={formMeta.fields.reason}>How can we help you?</SpsLabel>
          <SpsSelect formMeta={formMeta.fields.reason} value={formValue.reason} options={options} />
        </div>

        <div className="mb-2">
          <SpsLabel for={formMeta.fields.docParcelId}>Document or Parcel ID</SpsLabel>
          <SpsTextInput formMeta={formMeta.fields.docParcelId} value={formValue.docParcelId} />
        </div>

        <div className="mb-2">
          <SpsLabel for={formMeta.fields.tradingPartner}>Which relationship is this associated with?</SpsLabel>
          <SpsTextInput formMeta={formMeta.fields.tradingPartner} value={formValue.tradingPartner} />
        </div>
        <div className="mb-2">
          <SpsLabel for={formMeta.fields.additionalInfo}>Additional Information</SpsLabel>
          <SpsTextarea formMeta={formMeta.fields.additionalInfo} value={formValue.additionalInfo} rows={3} />
        </div>
      </>
    )
  };

  const otherGroupForm = (formMeta) => {

    return (
      <>
        <div className="mb-2">
          <SpsLabel for={formMeta.fields.reason}>How can we help you?</SpsLabel>
          <SpsTextInput formMeta={formMeta.fields.reason} value={formValue.reason} />
        </div>

        <div className="mb-2">
          <SpsLabel for={formMeta.fields.additionalInfo}>Additional Information</SpsLabel>
          <SpsTextarea formMeta={formMeta.fields.additionalInfo} value={formValue.additionalInfo} rows={3} />
        </div>
      </>
    )
  };

  function launchChat(event) {
    event.preventDefault();
    window['Intercom']('show');
    patchState({
      isOpen: false
    })
  }


  function fileUploadChange(event: CustomEvent<Array<File>>) {
    patchState({
      files: state.files.concat(event.detail)
    });
    createGrowler({
      kind: GrowlerKind.SUCCESS,
      title: "Success!",
      content: () => ("Your document(s) have been attached.")
    });

  }

  function productChangeHandler(event: any) {
    const product = event.target.value;
    setProductGroup(product);
    updateForm({ ...formValue, product: product, reason: undefined });
  }

  function setProductGroup(product) {
    switch (product) {
      case SF_MANAGED_APPS.FULFILLMENT:
      case SF_MANAGED_APPS.FULFILLMENT_MONITOR:
      case SF_MANAGED_APPS.COMMERCE_VAN:
        patchState({
          productGroup: "fulfillment"
        });
        break;

      case SF_MANAGED_APPS.TNC:
        patchState({
          productGroup: "tnc"
        });
        break;

      case SF_MANAGED_APPS.ASSORTMENT:
        patchState({
          productGroup: "assortment"
        });
        break;

      default:
        patchState({
          productGroup: "other"
        });
        break;
    }
  }

  const onFormSubmit = () => {
    if (state.loading) return;
    patchState({
      submitClasses: clsx("sps-button", "sps-button--confirm", "ml-1", "sps-button--spinning"),
      loading: true
    });
    const caseTypeMap = {
      [SF_MANAGED_APPS.FULFILLMENT]: 'Web Fulfillment',
      [SF_MANAGED_APPS.FULFILLMENT_MONITOR]: 'Fulfillment Monitor',
      [SF_MANAGED_APPS.COMMERCE_VAN]: 'Web Fulfillment',
      [SF_MANAGED_APPS.TNC]: 'EDI Testing and Certification',
      [SF_MANAGED_APPS.ASSORTMENT]: 'Assortment',
      [SF_MANAGED_APPS.OTHER]: 'Web Fulfillment'
    };
    const caseOwnersAndRecordTypes = {
      [SF_MANAGED_APPS.TNC]: {
        OwnerId: '00G70000001YsrS',
        RecordTypeId: '01270000000HjwCAAS'
      },
      [SF_MANAGED_APPS.ASSORTMENT]: {
        OwnerId: '00G70000001bKOK',
        RecordTypeId: '01270000000Hj6FAAS'
      },
      [SF_MANAGED_APPS.FULFILLMENT]: {
        OwnerId: '00G700000016XVn',
        RecordTypeId: '01270000000Hj6FAAS'
      },
      [SF_MANAGED_APPS.COMMERCE_VAN]: {
        OwnerId: '00G700000016XVn',
        RecordTypeId: '01270000000Hj6FAAS'
      },
      [SF_MANAGED_APPS.FULFILLMENT_MONITOR]: {
        OwnerId: '00G700000016XVn',
        RecordTypeId: '01270000000Hj6FAAS'
      },
      [SF_MANAGED_APPS.SOURCING]: {
        OwnerId: '00G70000001bKOK',
        RecordTypeId: '01270000000Hj6FAAS'
      },
      [SF_MANAGED_APPS.OTHER]: {
        OwnerId: '00G700000016XVn',
        RecordTypeId: '01270000000Hj6FAAS'
      }
    };
    const descriptionOutput = Object.keys(formValue).reduce((accum, key) => {
      const formItem = formValue[key];
      if (!formItem) return accum;
      if (_.isObject(formItem) && formItem.value) {
        return `${accum + key}: ${formItem.value} \n`;
      }
      return `${accum + key}: ${formItem} \n`;
    }, "");

    const datetime = moment().format('MMM D YYYY');
    const payload = {
      org_id: $CurrentUser.company.id,
      user_id: $CurrentUser.id,
      record_type_id: caseOwnersAndRecordTypes[formValue.product].RecordTypeId,
      origin: 'Contact Us', // this will trigger the sending of an email to customer
      case_type: caseTypeMap[formValue.product],
      subject: `${datetime}: ${formValue.product} | Commerce Platform contact form`,
      description: descriptionOutput,
      files: state.files
    };
    SalesforceService
      .createCase(payload)
      .then(() => {
        patchState({
          submitClasses: clsx("sps-button", "sps-button--confirm", "ml-1"),
          isOpen: false,
          loading: false
        });
        updateForm(initFormValue);
        sendSuccess();
      }).catch(failure => {
        createGrowler({
          title: "Message Creation Error",
          content: () => (<>Uh oh! Something went wrong... Please try again or <a href="#" onClick={launchChat}>contact us through chat</a>.</>),
          kind: GrowlerKind.ERROR
        });
        console.error(failure);
        patchState({
          submitClasses: clsx("sps-button", "sps-button--confirm", "ml-1"),
          loading: false
        });
      });
  }

  function sendSuccess() {
    patchState({
      submitSuccess: true
    });
  }

  const fullName = `${$CurrentUser.name.first} ${$CurrentUser.name.last}`;

  const stopEvent = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSupportClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    stopEvent(e);
    patchState({
      isOpen: true,
      preselectedProduct: getReferralApp(),
      displayProducts: getProducts($CurrentUser)
    });
  }

  window.addEventListener("openContactForm", event => {
    // Listen for the open contact form event, ignoring any non-string
    // messages like the ones generated by React Dev Tools.
    // Commerce Platform event data will always come in as a string
    patchState({
      isOpen: true,
      preselectedProduct: getReferralApp(),
      displayProducts: getProducts($CurrentUser)
    });
  });

  function closeFocusedTask() {
    patchState({ isOpen: false, productGroup: 'other' });
    updateForm(initFormValue);
  }

  React.useEffect(() => {
    if (fileUploadRef && fileUploadRef.current) {
      const uploadComponent = fileUploadRef;
      uploadComponent.current.addEventListener("selection", fileUploadChange);
      return () => {
        if (uploadComponent && uploadComponent.current) {
          uploadComponent.current.removeEventListener("selection", fileUploadChange)
        }
      }
    }

  });

  return portal(
    <div className="sps-contact-form">

      {/* This growler is hard-code b/c the hook wasn't always working */}
      {state.submitSuccess &&
        (<SpsGrowler
          kind={GrowlerKind.SUCCESS}
          title="Message Sent!"
          onClose={() => {
            patchState({
              submitSuccess: false
            })
          }}
        >A message has been sent on your behalf.</SpsGrowler>)}
      <a target="_blank" href="#" onClick={handleSupportClick} title="Support">
        {icon}
      </a>

      <SpsFocusedTask isOpen={state.isOpen} onClose={closeFocusedTask}>
        <SpsForm formMeta={formMeta} onSubmit={onFormSubmit}>
          {state.isLoading && <SpsLoadingState />}
          <div style={{ width: "50%", margin: "auto" }}>
            <h1>Contact Support</h1>
            <p className="description">Please complete the form below and a member of our support team will contact you soon, usually within one business day.</p>

            <SpsCard className="contact-form">
              <SpsCard>
                <section className="d-flex">
                  <section>
                    <SpsKeyValueList>
                      <SpsKeyValueListItem itemKey="Name">
                        {fullName}
                      </SpsKeyValueListItem>
                    </SpsKeyValueList>
                  </section>
                  <section>
                    <SpsKeyValueList>
                      <SpsKeyValueListItem itemKey="Company Name">
                        {$CurrentUser.company.name}
                      </SpsKeyValueListItem>
                    </SpsKeyValueList>
                  </section>
                </section>
                <section className="d-flex">
                  <section>
                    <SpsKeyValueList>
                      <SpsKeyValueListItem itemKey="Phone Number">
                        {$CurrentUser.profile.phoneNumber
                          ? $CurrentUser.profile.phoneNumber
                          : "No Phone Number"}
                      </SpsKeyValueListItem>
                    </SpsKeyValueList>
                  </section>
                  <section>
                    <SpsKeyValueList>
                      <SpsKeyValueListItem itemKey="Email Address">
                        {$CurrentUser.email}
                      </SpsKeyValueListItem>
                    </SpsKeyValueList>
                  </section>
                </section>
              </SpsCard>

              <div className="mb-2">
                <SpsLabel for={formMeta.fields.product}
                  errors={() =>
                    formMeta.fields.product.hasError("required") &&
                    "Please select the application or the type of inquiry you need support for."
                  }>
                  Inquiry Type/Application:
                  </SpsLabel>
                <SpsSelect options={state.displayProducts} formMeta={formMeta.fields.product} value={formValue.product} onChange={productChangeHandler} />
              </div>

              {state.productGroup === "fulfillment" ? fulfillmentGroupForm(formMeta) :
                state.productGroup === "tnc" ? tncGroupForm(formMeta) :
                  state.productGroup === "assortment" ? assortmentGroupForm(formMeta) :
                    otherGroupForm(formMeta)}

              <div className="mb-2">
                {/* using html label b/c sps-file-upload can not be target of SpsLabel for prop */}
                <label className="sps-form-group__label">
                  <span className="sps-form-group__label-content">
                    Add Attachment
                  </span>
                </label>
                <sps-file-upload
                  ref={fileUploadRef}
                  multiple
                  acceptExtensions="csv, xls, xlsx, doc, docx, pdf, txt, png, jpg, gif"
                  maxSize="10MB"
                />


              </div>

            </SpsCard>

            <SpsFocusedTaskActions>
              <div className="sps-button sps-button--default mr-1">
                <button type="button" onClick={closeFocusedTask} disabled={state.loading}>
                  Cancel
                      </button>
              </div>
              <div className={state.submitClasses}>
                <button type="submit" disabled={!formMeta.isValid()}>
                  Submit
                  {state.loading && (<div className="sps-spinner"></div>)}
                </button>
              </div>
            </SpsFocusedTaskActions>

          </div>
        </SpsForm>
      </SpsFocusedTask>
      {growlers()}
    </div>
  );
}
